// client/src/pages/secure/BrecAppAdminPage.js — My Office -> The BREC App (/dashboard/app).
//
// Everyone: how to install the app on each device, and the link to hand out (/app).
// Managing broker: the Android app file — publish a newly built APK (it is served at
// /app/android) and see whether the signing fingerprint is set (Integrations).
import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useApiClient } from '../../hooks/useApiClient';
import { getCurrentUser } from '../../hooks/useCurrentUser';
import BrecAppGuide from '../../components/shared/BrecAppGuide';

const mb = (n) => `${(n / (1024 * 1024)).toFixed(1)} MB`;

const BrecAppAdminPage = () => {
    const api = useApiClient();
    const user = getCurrentUser();
    const isBroker = !!user?.isManagingBroker && user?.role !== 'examiner';
    const [status, setStatus] = useState(null);
    const [file, setFile] = useState(null);
    const [msg, setMsg] = useState('');
    const [busy, setBusy] = useState(false);
    const shareLink = `${window.location.origin}/app`;

    const load = useCallback(() => {
        api.get('/api/brec-app/status').then(setStatus).catch(() => setStatus(null));
    }, [api]);
    useEffect(() => { load(); }, [load]);

    const upload = async (e) => {
        e.preventDefault();
        if (!file) return;
        setBusy(true); setMsg('');
        try {
            const fd = new FormData();
            fd.append('apk', file);
            const r = await api.post('/api/brec-app/android-apk', fd, true);
            setMsg(`Published (${mb(r.bytes)}). The download link now gives this file.`);
            setFile(null);
            load();
        } catch (err) {
            setMsg(err.message);
        } finally { setBusy(false); }
    };

    const copy = () => { try { navigator.clipboard.writeText(shareLink); setMsg('Link copied.'); } catch { /* ignore */ } };

    return (
        <div className="max-w-5xl mx-auto space-y-6">
            <div>
                <h2 className="text-2xl font-bold text-slate-800">The BREC App</h2>
                <p className="text-sm text-slate-600 mt-1">The whole portal as an app on phones, tablets and computers. It loads the live portal, so every portal update is in the app straight away.</p>
            </div>

            <section className="bg-white rounded-lg shadow-sm border border-slate-200 p-4 sm:p-5">
                <h3 className="text-base font-semibold text-slate-800">Link to hand out</h3>
                <p className="text-sm text-slate-600 mt-1">Send this to staff, agents or clients (text or email). It explains how to install on their device.</p>
                <div className="mt-3 flex flex-col sm:flex-row gap-2">
                    <input readOnly value={shareLink} className="flex-1 min-w-0 p-2 border border-slate-300 rounded-md text-sm bg-slate-50" onFocus={(e) => e.target.select()} />
                    <button type="button" onClick={copy} className="px-4 py-2 min-h-[40px] bg-slate-800 text-white rounded-md text-sm">Copy link</button>
                </div>
            </section>

            {isBroker && (
                <section className="bg-white rounded-lg shadow-sm border border-slate-200 p-4 sm:p-5">
                    <h3 className="text-base font-semibold text-slate-800">Android app file (managing broker)</h3>
                    {status ? (
                        <ul className="text-sm text-slate-700 mt-2 space-y-1">
                            <li>App: <strong>{status.android.appName}</strong> <span className="text-slate-500">({status.android.packageName})</span></li>
                            <li>Published file: {status.apk.available
                                ? <>yes — {mb(status.apk.bytes)}, {new Date(status.apk.uploaded_at).toLocaleString()} · <a href="/app/android" className="text-indigo-700 underline">download</a></>
                                : <span className="text-amber-700">none yet</span>}</li>
                            <li>Signing fingerprint for full-screen mode: {status.android.fingerprintsSet
                                ? <span className="text-green-700">set</span>
                                : <span className="text-amber-700">not set — <Link to="/dashboard/integrations" className="underline">Administration → Integrations → BREC Android app</Link></span>}</li>
                        </ul>
                    ) : <p className="text-sm text-slate-500 mt-2">Loading…</p>}
                    <form onSubmit={upload} className="mt-4 flex flex-col sm:flex-row sm:items-center gap-2">
                        <input type="file" accept=".apk,application/vnd.android.package-archive" onChange={(e) => setFile(e.target.files[0] || null)} className="text-sm min-w-0" />
                        <button type="submit" disabled={!file || busy} className="px-4 py-2 min-h-[40px] bg-slate-800 text-white rounded-md text-sm disabled:opacity-50">{busy ? 'Publishing…' : 'Publish this APK'}</button>
                    </form>
                    <p className="text-xs text-slate-500 mt-2">Only needed when the app shell itself is rebuilt (rare) — portal updates reach the app without a new file. Build steps: mobile/android/README.md.</p>
                </section>
            )}
            {msg && <div className="text-sm text-slate-700">{msg}</div>}

            <BrecAppGuide apkAvailable={status ? status.apk.available : null} />
        </div>
    );
};

export default BrecAppAdminPage;
